import { Controller } from "@hotwired/stimulus"

/**
 * Category / Subcategory picker + manager for the ConsLog Builder project form.
 *
 * Each field is an autocomplete-style combobox (type to filter, results panel,
 * "+ Manage…" footer). The footer opens a modal where any company member can
 * add / rename / delete categories (Portfolios) and subcategories (Programs).
 * Deletes that are in use are blocked by the server (422) and shown inline.
 * CRUD hits the Builder::Categories / Builder::Subcategories endpoints.
 */
export default class extends Controller {
  static targets = [
    // Category combobox
    "catInput", "catHidden", "catPanel", "catResults", "catEmpty",
    // Subcategory combobox
    "subInput", "subHidden", "subPanel", "subResults", "subEmpty",
    // Modal
    "modal", "mError", "mCatList", "mSubTitle", "mSubEmpty", "mSubList",
    "mSubAddWrap", "mCatAddInput", "mSubAddInput"
  ]
  static values = {
    categories: Array,
    catCreateUrl: String, catItemTemplate: String,
    subCreateUrl: String, subItemTemplate: String, subListTemplate: String
  }

  connect() {
    this.cats = [...(this.categoriesValue || [])]
    this.subs = []          // form subcategory options for the selected category
    this.selectedCatId = null
    this.modalCatId = null
    this._outside = (e) => { if (!this.element.contains(e.target)) { this.closeCat(); this.closeSub() } }
    document.addEventListener("click", this._outside)
  }
  disconnect() { document.removeEventListener("click", this._outside) }

  // ── Category combobox ─────────────────────────────────────────────
  openCat() { this._renderCatResults(this.catInputTarget.value); this.catPanelTarget.classList.remove("hidden") }
  closeCat() { this.catPanelTarget.classList.add("hidden") }
  filterCat() { this._renderCatResults(this.catInputTarget.value); this.catPanelTarget.classList.remove("hidden") }
  catKeydown(e) { if (e.key === "Escape") this.closeCat(); if (e.key === "Enter") e.preventDefault() }

  _renderCatResults(q) {
    const f = this._filter(this.cats, q)
    this.catResultsTarget.innerHTML = f.map((c) => this._row("pickCat", c.id, c.name)).join("")
    this.catEmptyTarget.classList.toggle("hidden", f.length > 0)
  }
  pickCat(e) { const { id, name } = e.currentTarget.dataset; this._setCat(id, name); this.closeCat() }
  _setCat(id, name) {
    this.catHiddenTarget.value = id || ""
    this.catInputTarget.value = name || ""
    this.selectedCatId = id || null
    this._enableSub(!!id)
    if (id) this._loadFormSubs(id)
  }

  // ── Subcategory combobox ──────────────────────────────────────────
  _enableSub(on) {
    this.subs = []
    this.subHiddenTarget.value = ""
    this.subInputTarget.value = ""
    this.subInputTarget.disabled = !on
    this.subInputTarget.placeholder = on ? "Search or select a subcategory…" : "Select a category first…"
    this.subInputTarget.classList.toggle("bg-gray-50", !on)
    this.subInputTarget.classList.toggle("text-gray-500", !on)
    this.subInputTarget.classList.toggle("bg-white", on)
  }
  async _loadFormSubs(catId) {
    this.subs = (await this._get(this.subListTemplateValue.replace("__ID__", catId))) || []
  }
  openSub() { if (this.subInputTarget.disabled) return; this._renderSubResults(this.subInputTarget.value); this.subPanelTarget.classList.remove("hidden") }
  closeSub() { this.subPanelTarget.classList.add("hidden") }
  filterSub() { this._renderSubResults(this.subInputTarget.value); this.subPanelTarget.classList.remove("hidden") }
  subKeydown(e) { if (e.key === "Escape") this.closeSub(); if (e.key === "Enter") e.preventDefault() }

  _renderSubResults(q) {
    const f = this._filter(this.subs, q)
    this.subResultsTarget.innerHTML = f.map((s) => this._row("pickSub", s.id, s.name)).join("")
    this.subEmptyTarget.classList.toggle("hidden", f.length > 0)
  }
  pickSub(e) { const { id, name } = e.currentTarget.dataset; this.subHiddenTarget.value = id || ""; this.subInputTarget.value = name || ""; this.closeSub() }

  _row(action, id, name) {
    return `<li><button type="button" data-action="category-manager#${action}" data-id="${id}" data-name="${this._esc(name)}" class="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 truncate">${this._esc(name)}</button></li>`
  }
  _filter(arr, q) { q = (q || "").trim().toLowerCase(); return q ? arr.filter((x) => x.name.toLowerCase().includes(q)) : arr }

  // ── Modal ─────────────────────────────────────────────────────────
  openCategoriesModal() { this.closeCat(); this._openModal() }
  openSubcategoriesModal() { this.closeSub(); this._openModal(this.selectedCatId) }
  _openModal(focusCatId = null) {
    this._clearErr(); this._renderMCats()
    if (focusCatId) this._selectMCat(focusCatId); else this._showMSubEmpty()
    this.modalTarget.classList.remove("hidden")
  }
  hideModal() { this.modalTarget.classList.add("hidden") }
  stopPropagation(e) { e.stopPropagation() }

  _renderMCats() {
    this.mCatListTarget.innerHTML = this.cats.map((c) => this._mCatRow(c.id, c.name)).join("") ||
      `<li class="px-2.5 py-1.5 text-[13px] text-gray-400">No categories yet.</li>`
  }
  _mCatRow(id, name) {
    const active = String(id) === String(this.modalCatId) ? "bg-brand/10" : ""
    return `<li data-row data-id="${id}" class="group flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md hover:bg-gray-50 ${active}">
      <div data-row-main class="flex items-center justify-between w-full gap-2">
        <button type="button" data-action="category-manager#mSelectCat" data-id="${id}" class="flex-1 text-left text-[13px] text-gray-800 truncate">${this._esc(name)}</button>
        <span class="flex items-center gap-2 shrink-0">
          <button type="button" data-action="category-manager#mRenameCat" class="text-[13px] text-gray-400 hover:text-gray-700">Rename</button>
          <button type="button" data-action="category-manager#mDeleteCat" class="text-[13px] text-gray-400 hover:text-red-600">Delete</button>
        </span>
      </div></li>`
  }
  mSelectCat(e) { this._selectMCat(e.currentTarget.dataset.id) }
  async _selectMCat(id) {
    this.modalCatId = String(id)
    const name = (this.cats.find((c) => String(c.id) === String(id)) || {}).name || ""
    this.mSubTitleTarget.textContent = `Subcategories of “${name}”`
    this._renderMCats()
    this.mSubEmptyTarget.classList.add("hidden")
    this.mSubListTarget.classList.remove("hidden")
    this.mSubAddWrapTarget.classList.remove("hidden")
    this.mSubListTarget.innerHTML = `<li class="px-2.5 py-1.5 text-[13px] text-gray-400">Loading…</li>`
    const rows = await this._get(this.subListTemplateValue.replace("__ID__", id))
    this.modalSubs = rows || []
    this._renderMSubs()
    if (String(this.selectedCatId) === String(id)) this.subs = rows || []  // keep form combobox in sync
  }
  _showMSubEmpty() {
    this.mSubTitleTarget.textContent = "Subcategories"
    this.mSubListTarget.classList.add("hidden")
    this.mSubAddWrapTarget.classList.add("hidden")
    this.mSubEmptyTarget.classList.remove("hidden")
  }
  _renderMSubs() {
    this.mSubListTarget.innerHTML = (this.modalSubs || []).map((s) => this._mSubRow(s.id, s.name)).join("") ||
      `<li class="px-2.5 py-1.5 text-[13px] text-gray-400">No subcategories yet.</li>`
  }
  _mSubRow(id, name) {
    return `<li data-row data-id="${id}" class="group flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md hover:bg-gray-50">
      <div data-row-main class="flex items-center justify-between w-full gap-2">
        <span data-name class="flex-1 text-[13px] text-gray-800 truncate">${this._esc(name)}</span>
        <span class="flex items-center gap-2 shrink-0">
          <button type="button" data-action="category-manager#mRenameSub" class="text-[13px] text-gray-400 hover:text-gray-700">Rename</button>
          <button type="button" data-action="category-manager#mDeleteSub" class="text-[13px] text-gray-400 hover:text-red-600">Delete</button>
        </span>
      </div></li>`
  }

  // Category CRUD
  mCatAddKeydown(e) { if (e.key === "Enter") { e.preventDefault(); this.mAddCat() } }
  async mAddCat() {
    const name = this.mCatAddInputTarget.value.trim(); if (!name) return
    const d = await this._req(this.catCreateUrlValue, "POST", { portfolio: { name } }); if (!d) return
    this.cats.push({ id: d.id, name: d.name }); this.mCatAddInputTarget.value = ""; this._renderMCats()
  }
  mRenameCat(e) { this._beginRename(e, (id, name) => this._saveCatRename(id, name)) }
  async _saveCatRename(id, name) {
    const d = await this._req(this.catItemTemplateValue.replace("__ID__", id), "PATCH", { portfolio: { name } })
    if (!d) { this._renderMCats(); return }
    const c = this.cats.find((x) => String(x.id) === String(id)); if (c) c.name = name
    if (String(this.selectedCatId) === String(id)) this.catInputTarget.value = name
    if (String(this.modalCatId) === String(id)) this.mSubTitleTarget.textContent = `Subcategories of “${name}”`
    this._renderMCats()
  }
  async mDeleteCat(e) {
    const id = e.currentTarget.closest("[data-row]").dataset.id
    const d = await this._req(this.catItemTemplateValue.replace("__ID__", id), "DELETE"); if (!d) return
    this.cats = this.cats.filter((x) => String(x.id) !== String(id))
    if (String(this.selectedCatId) === String(id)) this._setCat("", "")
    if (String(this.modalCatId) === String(id)) { this.modalCatId = null; this._showMSubEmpty() }
    this._renderMCats()
  }

  // Subcategory CRUD
  mSubAddKeydown(e) { if (e.key === "Enter") { e.preventDefault(); this.mAddSub() } }
  async mAddSub() {
    if (!this.modalCatId) return
    const name = this.mSubAddInputTarget.value.trim(); if (!name) return
    const d = await this._req(this.subCreateUrlValue, "POST", { program: { name, portfolio_id: this.modalCatId } }); if (!d) return
    this.mSubAddInputTarget.value = ""; await this._selectMCat(this.modalCatId)
  }
  mRenameSub(e) { this._beginRename(e, (id, name) => this._saveSubRename(id, name)) }
  async _saveSubRename(id, name) {
    await this._req(this.subItemTemplateValue.replace("__ID__", id), "PATCH", { program: { name } })
    if (this.modalCatId) await this._selectMCat(this.modalCatId)
  }
  async mDeleteSub(e) {
    const id = e.currentTarget.closest("[data-row]").dataset.id
    const d = await this._req(this.subItemTemplateValue.replace("__ID__", id), "DELETE"); if (!d) return
    if (this.modalCatId) await this._selectMCat(this.modalCatId)
  }

  // Shared inline rename
  _beginRename(e, onSave) {
    e.stopPropagation()
    const row = e.currentTarget.closest("[data-row]")
    const cur = (row.querySelector("[data-name]")?.textContent || row.querySelector("button[data-id]")?.textContent || "").trim()
    const main = row.querySelector("[data-row-main]")
    main.innerHTML = `<div class="flex items-center gap-1 w-full">
      <input type="text" data-rename-input class="flex-1 px-2 py-1 text-[13px] border border-gray-200 rounded" value="${this._esc(cur)}">
      <button type="button" data-action="category-manager#commitRename" class="px-2 py-1 text-[13px] bg-brand text-white rounded">Save</button>
    </div>`
    const input = main.querySelector("[data-rename-input]")
    input.focus()
    this._pending = { id: row.dataset.id, onSave }
    input.addEventListener("keydown", (ev) => { if (ev.key === "Enter") { ev.preventDefault(); this.commitRename({ currentTarget: input }) } })
  }
  commitRename(e) {
    const input = e.currentTarget.closest("[data-row-main]").querySelector("[data-rename-input]")
    const name = input.value.trim()
    const { id, onSave } = this._pending || {}
    if (name && onSave) onSave(id, name)
  }

  // Networking
  _clearErr() { this.mErrorTarget.classList.add("hidden"); this.mErrorTarget.textContent = "" }
  _showErr(m) { this.mErrorTarget.textContent = m; this.mErrorTarget.classList.remove("hidden") }
  async _get(url) {
    try { const r = await fetch(url, { headers: { Accept: "application/json" } }); return r.ok ? await r.json() : null }
    catch (_) { return null }
  }
  async _req(url, method, body) {
    this._clearErr()
    try {
      const r = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" },
        body: body ? JSON.stringify(body) : undefined
      })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) { this._showErr(d.error || (d.errors && d.errors.join(", ")) || "Something went wrong."); return null }
      return d
    } catch (_) { this._showErr("Network error — please try again."); return null }
  }
  _esc(s) { const d = document.createElement("div"); d.textContent = s; return d.innerHTML.replace(/"/g, "&quot;") }
}
